import { type FC, useState, useEffect, useCallback } from "react";
import { usePouch } from "use-pouchdb";
import { Link } from "react-router";

import { isItemInfo } from "../../common/schema";
import { loadAllConflicts } from "../conflicts";

interface DataError {
  id: string;
  name: string;
  error: string;
}

export const DataErrors: FC = () => {
  const db = usePouch();
  const [errors, setErrors] = useState<DataError[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [conflictCount, setConflictCount] = useState<number | null>(null);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setCheckErr(null);
    try {
      const found: DataError[] = [];
      const conflicted = await loadAllConflicts(db);
      setConflictCount(conflicted.length);

      // Items failing schema validation (covers invalid shelcodes, bad types, etc.)
      const invalidResult = await db.query<Record<string, unknown>>("built/invalid_docs", { include_docs: true });
      for (const row of invalidResult.rows) {
        if (!row.id.startsWith("item:")) continue;
        const name = typeof row.doc?.["name"] === "string" ? row.doc["name"] : row.id;
        found.push({ id: row.id, name, error: `schema invalid: ${row.value}` });
      }

      // Items where `in` references a missing or deleted parent
      const allResult = await db.allDocs<Record<string, unknown>>({
        startkey: "item:",
        endkey: "item:￿",
        include_docs: true,
      });
      const allIds = new Set<string>(allResult.rows.map(r => r.id));
      const nonDeleted = new Set<string>();
      for (const row of allResult.rows) {
        if (row.doc?.["deleted_at"] == null) nonDeleted.add(row.id);
      }
      for (const row of allResult.rows) {
        if (!isItemInfo(row.doc)) continue; // already in schema errors above
        if (row.doc.deleted_at != null) continue; // deleted items may dangle freely
        if (row.doc.in == null || row.doc.in === "") continue;
        if (!allIds.has(row.doc.in)) {
          found.push({ id: row.id, name: row.doc.name, error: `"in" references missing item ${row.doc.in}` });
        } else if (!nonDeleted.has(row.doc.in)) {
          found.push({ id: row.id, name: row.doc.name, error: `"in" references deleted item ${row.doc.in}` });
        }
      }

      setErrors(found);
    } catch (e) {
      setCheckErr(`${e}`);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => { void runCheck(); }, [runCheck]);

  return (
    <div style={{ padding: "6px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <h3 style={{ margin: 0 }}>Data errors</h3>
        <button type="button" onClick={() => void runCheck()} disabled={loading}>
          {loading ? "Checking…" : "Refresh"}
        </button>
      </div>
      {checkErr && <p style={{ color: "red" }}>{checkErr}</p>}
      {conflictCount != null && conflictCount > 0 && (
        <p style={{ color: "orange", margin: "4px 0" }}>
          {conflictCount} document{conflictCount !== 1 ? "s" : ""} with unresolved conflicts.{" "}
          <Link to="/conflicts">Resolve conflicts →</Link>
        </p>
      )}
      {errors === null && !checkErr && <p>Checking…</p>}
      {errors !== null && errors.length === 0 && conflictCount === 0 && <p style={{ color: "green" }}>No errors found.</p>}
      {errors !== null && errors.length > 0 && (
        <ul>
          {errors.map((e, i) => (
            <li key={i}>
              <Link to={`/items/${e.id}`}>{e.name || e.id}</Link>: {e.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
